import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Space,
  Stack,
} from '@tonic-one/react';
import { Parser } from '@json2csv/plainjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EuqFeedbackRecord } from '../../../type';
import { formatCecpRecords, FormattedCecpData } from '../utils';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { OperationsApi } from '@Apis';
import { EUQ_EXPORT_LIMIT } from '../../constants';
import {
  buildEuqFeedbackFilterParams,
  EuqFeedbackFilterParams,
} from '../../hooks/useEuqFeedbackQuery';

interface Props {
  visible: boolean;
  onClose: () => void;
  columns: { id: string; header: string }[];
  data: EuqFeedbackRecord[];
  filterParams: EuqFeedbackFilterParams;
}

type CsvRecord = Record<string, string>;

const ExportModal = (props: Props) => {
  const { visible, onClose, columns, data, filterParams } = props;
  const [exportType, setExportType] = useState('');
  const [exportBtnDisabled, setExportBtnDisabled] = useState(true);
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();

  const onRadioChanged = (value: string) => {
    setExportType(value);
    setExportBtnDisabled(false);
  };

  const reset = () => {
    setExportType('');
    setExportBtnDisabled(true);
  };

  const closeModal = () => {
    onClose();
    reset();
  };

  const exportCSVFile = (records: CsvRecord[]) => {
    const csv = new Parser().parse(records);
    const url = window.URL.createObjectURL(
      new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'euq-cecp-data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseData = (records: FormattedCecpData[]) => {
    const exportData: CsvRecord[] = [];
    const emptyData: CsvRecord = {};
    for (const column of columns) {
      emptyData[column.header] = '';
    }
    for (const item of records) {
      const rowData: CsvRecord = { ...emptyData };
      const cells = item as unknown as Record<string, unknown>;
      for (const column of columns) {
        const value = cells[column.id];
        rowData[column.header] =
          value === null || value === undefined ? '' : String(value);
      }
      exportData.push(rowData);
    }
    if (exportData.length === 0) {
      exportData.push(emptyData);
    }
    return exportData;
  };

  const exportLog = async () => {
    closeModal();
    try {
      const timeFormat = t('common.momentDateFormat');

      // 'Current view' exports the loaded page; 'All records' refetches the
      // full matching set (up to EUQ_EXPORT_LIMIT) with the active filters.
      let records: EuqFeedbackRecord[] = data;
      if (exportType === '1') {
        const response = await OperationsApi.apiExportEuqContentList({
          ...buildEuqFeedbackFilterParams(filterParams),
          offset: 0,
          limit: EUQ_EXPORT_LIMIT,
        });
        records = response.data.contentList;
      }

      // Use shared utility to format data consistently with table
      const formattedData = formatCecpRecords(records, offset, timeFormat, t);
      const csvData = parseData(formattedData);
      exportCSVFile(csvData);
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.error'),
        bodyMsg: t('common.exportError'),
        severity: 'error',
      });
      console.error(error);
    }
  };

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={visible}
      onClose={closeModal}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('operations.euq.export.title')}</ModalHeader>
        <ModalBody>
          <Box>
            <RadioGroup value={exportType} onChange={onRadioChanged}>
              <Stack spacing="1x" shouldWrapChildren>
                <Radio value="0">{t('log.modals.export.currentView')}</Radio>
                <Radio value="1">
                  {t('log.modals.export.allRecordsNoMaximum')}
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            minWidth="20x"
            variant="primary"
            disabled={exportBtnDisabled}
            onClick={exportLog}
          >
            {t('common.action.label.export')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={closeModal}>
            {t('common.action.label.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default ExportModal;
